// ============================================================
// STARLINK V2 API CLIENT
// ============================================================
// Two modes:
//   1. Mock mode (current default) — reads from src/data/mock/*.json
//   2. Live mode — calls AWS API Gateway, which proxies the real
//      Starlink V2 API at https://web-api.starlink.com
//
// Mode is controlled by VITE_USE_MOCK_API env var.
// Real-mode credentials NEVER live in this file or the bundle —
// the bundle calls our backend, which fetches Starlink creds
// from AWS Secrets Manager at runtime.

import accountsMock from '../data/mock/accounts.json';
import serviceLinesMock from '../data/mock/service-lines.json';
import terminalsMock from '../data/mock/terminals.json';
import telemetryMock from '../data/mock/telemetry.json';
import billingMock from '../data/mock/billing.json';
import { getVertical, VERTICALS, allVerticalTerminals } from '../data/mock/verticals.js';
import { sanitizePricing } from './compliance.js';
import { getSession } from './auth.js';

// ── Vertical demo fleets ────────────────────────────────────
// Three distinct demo customers (Globaltrade Holdings subsidiaries), each
// with its own fleet shape so the Org-switcher / "View as customer" shows
// genuinely different data:
//   • Globaltrade Energy   — Oil & Gas, LARGE (~64 sites)  · flagship demo
//   • Globaltrade Logistics— Mobile fleet, MEDIUM (~24 units)
//   • Globaltrade Retail   — Storefront failover, SMALL (~10 stores)
// getVertical() resolves an account number (incl. the legacy
// ACC-CTO-50-FLEET alias) to its fleet bundle, or null for non-demo
// accounts. Replaces the old single-fleet ENTERPRISE_* model where all
// three subsidiaries aliased to the same logistics dataset.
export function isEnterpriseAccount(accountNumber) {
  return !!getVertical(accountNumber);
}
/** Resolve account → fleet bundle { account, terminals, stats, groups } or null. */
function resolveFleet(accountNumber) {
  return getVertical(accountNumber);
}

const USE_MOCK = import.meta.env.VITE_USE_MOCK_API !== 'false';
const API_BASE = import.meta.env.VITE_API_BASE || '/api';

// Demo-customer override: when a netcom-admin starts a demo session
// (see lib/demo-customer.js + Sidebar "Open Demo Customer" entry),
// every call here gets routed through the mock branch — so the demo
// works even when the real API is unreachable. Checked per-call so
// starting/ending the demo doesn't require a hard reload.
function inDemoSession() {
  try {
    return typeof sessionStorage !== 'undefined'
      && !!sessionStorage.getItem('netcom_connect_demo_customer_session');
  } catch { return false; }
}
function isMockMode() { return USE_MOCK || inDemoSession(); }

// Public alias for UI honesty labeling — surfaces that render synthesized
// data (telemetry charts, topology, trends) show a "Simulated data" pill
// while this is true, and drop it automatically once the real feed is live.
// Real-only actions (reboot dispatch) also gate on it. Panel review
// 2026-06-11: unlabeled synthetic data reads as fabricated capability.
export function isSimulatedData() { return isMockMode(); }

// Simulate network latency for realistic UI loading states
const MOCK_LATENCY_MS = 250;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// ─── Live API helpers (used when USE_MOCK is false) ───────────

async function liveGet(path) {
  const session = getSessionToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Authorization': session ? `Bearer ${session}` : '',
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
  return res.json();
}

async function livePost(path, body) { return liveSend('POST', path, body); }
async function livePut(path, body) { return liveSend('PUT', path, body); }
async function liveSend(method, path, body) {
  const session = getSessionToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Authorization': session ? `Bearer ${session}` : '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.message || ''; } catch { /* ignore */ }
    const err = new Error(detail || `API ${res.status}: ${path}`);
    err.status = res.status;
    throw err;
  }
  return res.status === 204 ? null : res.json().catch(() => ({}));
}

function getSessionToken() {
  // API Gateway's CognitoUserPoolsAuthorizer validates ID tokens (not access
  // tokens — that's a common Cognito gotcha). Access tokens are for OAuth
  // scopes; ID tokens carry the user-identity claims (custom:*, email,
  // groups) that the authorizer + downstream Lambdas need. Sending the
  // access token here causes every authenticated call to return 401.
  try {
    const session = localStorage.getItem('netcom_connect_session');
    if (!session) return null;
    const s = JSON.parse(session);
    return s.idToken || s.accessToken || null;
  } catch {
    return null;
  }
}

// ─── Public API surface (used by pages/components) ────────────

/**
 * Get all accounts this user has access to.
 * In live mode: maps to /public/v1/accounts (filtered server-side
 * by the customer's Cognito identity claim).
 */
export async function getAccounts() {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    const verticalAccounts = Object.values(VERTICALS).map((v) => v.account);
    return [...accountsMock.accounts, ...verticalAccounts];
  }
  try {
    const data = await liveGet('/accounts');
    return data.accounts || [];
  } catch (e) {
    console.error('[starlink-api] getAccounts failed:', e?.message);
    return [];
  }
}

/**
 * Get a single account by accountNumber. Demo aliases for the enterprise
 * fleet are resolved here so the Dashboard / Service Lines / Network pages
 * find a match when the Cognito custom:starlink_acct_no doesn't exactly
 * equal ACC-CTO-50-FLEET.
 */
export async function getAccount(accountNumber) {
  const fleet = resolveFleet(accountNumber);
  if (fleet) return fleet.account;
  const all = await getAccounts();
  return all.find((a) => a.accountNumber === accountNumber) || null;
}

/**
 * Get all service lines for an account.
 * Maps to /public/v1/account/{accountNumber}/service-lines.
 */
export async function getServiceLines(accountNumber) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    const fleet = resolveFleet(accountNumber);
    if (fleet) {
      // Map this vertical's terminals into service-line shape
      return fleet.terminals.map((t) => ({
        serviceLineNumber: t.serviceLineNumber,
        nickname: t.siteName,
        addressReferenceId: `addr-${t.userTerminalId}`,
        addressDescription: t.addressDescription,
        planName: t.planTier,
        planId: t.planTier.toLowerCase().replace(/\s+/g, '-'),
        priority: t.planTier.includes('Global') ? 'Global' : t.planTier.includes('Mobile') || t.planTier.includes('Roam') ? 'Mobile' : 'Local',
        optedIn: t.siteType !== 'backup',
        active: t.status !== 'offline',
        createdAt: t.activatedAt,
        currentCycle: {
          startedAt: '2026-05-01T00:00:00Z',
          endsAt: '2026-06-01T00:00:00Z',
          dataAllocatedGB: t.monthlyAllocationGB,
          dataUsedGB: t.monthlyUsageGB,
          topUpsThisCycleGB: t.monthlyUsageGB > t.monthlyAllocationGB ? 50 : 0,
        },
      }));
    }
    return serviceLinesMock[accountNumber] || [];
  }
  // Live mode — Starlink V2 swagger path (verified against research
  // synthesis 2026-05-29): GET /public/v1/account/{accountNumber}/service-lines
  // (singular `/account/` for service-lines and addresses; plural
  // `/accounts/` only for account-root + billing-cycles).
  //
  // Our API Gateway proxies this 1:1 — the Lambda still derives
  // accountNumber from the JWT for tenant isolation, but the path
  // mirrors upstream so the proxy stays trivial. Field aliasing
  // kept so a Lambda that hasn't been updated still returns data.
  // Real route (Peplink-backed, token-scoped): GET /service-lines.
  // Fail-safe: a backend hiccup returns [] rather than throwing into a page
  // that may not catch — no widget should ever hang on this.
  try {
    const data = await liveGet('/service-lines');
    return data.lines || data.serviceLines || data.results || [];
  } catch (e) {
    console.error('[starlink-api] getServiceLines failed:', e?.message);
    return [];
  }
}

/**
 * Get all user terminals (dishes) for an account.
 * Swagger path: GET /public/v1/account/{accountNumber}/user-terminals
 *
 * Naming note: Starlink calls these "user terminals". Our UI calls them
 * "dishes" (customer-facing). Internally we still use `deviceId` as the
 * primary key. NEW (2026-05-29 research): every terminal has BOTH a
 * `deviceId` AND a `routerId` — the routerId is what the reboot + router-
 * config endpoints expect. We surface both fields on the model now so the
 * UI/Lambda layer can pass the right one to the right endpoint.
 */
export async function getTerminals(accountNumber) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    const fleet = resolveFleet(accountNumber);
    if (fleet) return fleet.terminals;
    return terminalsMock[accountNumber] || [];
  }
  // MULTI-ACCOUNT: a customer can own more than one Starlink account under
  // Netcom's ASP relationship (confirmed 2026-08-05 for Radius Security —
  // dishes on their second account were invisible with the single-account
  // route below). When the signed-in session carries more than one, hit
  // the aggregate GET /terminals route instead so every page that already
  // calls getTerminals(accountNumber) gets the FULL fleet for free, no
  // per-page changes needed. Falls through to the single-account route
  // for everyone else (the common case) and for any explicit accountNumber
  // that isn't the session's own (admin impersonation, demo, site-detail
  // deep links) — those must stay scoped to exactly the one requested.
  const session = getSession();
  const ownAccounts = session?.accountNumbers;
  const isOwnScope = accountNumber === session?.accountNumber;
  if (Array.isArray(ownAccounts) && ownAccounts.length > 1 && isOwnScope) {
    try {
      const data = await liveGet('/terminals');
      return data.terminals || [];
    } catch (e) {
      console.error('[starlink-api] getTerminals (aggregate) failed:', e?.message);
      return [];
    }
  }
  // Real route (Peplink-backed): GET /accounts/{accountNumber}/terminals.
  // Fail-safe → [] so the dashboard/fleet pages render even if this errors.
  try {
    const data = await liveGet(`/accounts/${encodeURIComponent(accountNumber)}/terminals`);
    return data.terminals || data.userTerminals || data.results || [];
  } catch (e) {
    console.error('[starlink-api] getTerminals failed:', e?.message);
    return [];
  }
}

/**
 * Get live telemetry for a single terminal.
 * Maps to Get Telemetry endpoint (V2).
 */
export async function getTelemetry(deviceId) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    // Try the static mock first (for the standard accounts)
    if (telemetryMock[deviceId]) return telemetryMock[deviceId];
    // Then look across every vertical fleet's terminals
    const t = allVerticalTerminals().find((x) => x.deviceId === deviceId);
    if (!t) return null;
    if (t.status === 'offline') {
      return {
        deviceId,
        offline: true,
        popPingLatencyMs: null,
        downlinkThroughputMbps: null,
        uplinkThroughputMbps: null,
        signalQualityPercent: null,
        obstructionFractionPercent: null,
        outageDurationLast24hSeconds: 86400,
        snrDb: null,
        softwareVersion: t.softwareVersion,
        fetchedAt: new Date().toISOString(),
        history24h: [],
      };
    }
    // Synthesize 12-point 24h history
    const history = Array.from({ length: 12 }, (_, i) => ({
      t: new Date(Date.now() - (12 - i) * 2 * 3600 * 1000).toISOString(),
      latencyMs: t.popPingLatencyMs + (Math.sin(i * 0.7) * 3) + (Math.random() - 0.5) * 2,
      downMbps: t.downlinkThroughputMbps + (Math.sin(i * 0.5) * 12) + (Math.random() - 0.5) * 6,
      upMbps: t.uplinkThroughputMbps + (Math.sin(i * 0.6) * 2) + (Math.random() - 0.5),
    }));
    return {
      deviceId,
      fetchedAt: new Date().toISOString(),
      popPingLatencyMs: t.popPingLatencyMs,
      downlinkThroughputMbps: t.downlinkThroughputMbps,
      uplinkThroughputMbps: t.uplinkThroughputMbps,
      signalQualityPercent: t.signalQualityPercent,
      obstructionFractionPercent: t.obstructionPercent,
      outageDurationLast24hSeconds: t.status === 'degraded' ? Math.floor(Math.random() * 300) : Math.floor(Math.random() * 30),
      snrDb: t.snrDb,
      softwareVersion: t.softwareVersion,
      history24h: history,
    };
  }
  // The snapshot reads the MOST RECENT sample from our telemetry history store
  // (populated continuously by the scheduled ingest Lambda) rather than polling
  // Starlink's live telemetry/stream directly. Critical correctness fix: the
  // Starlink telemetry/stream is a SINGLE per-account cursor — if the SPA polled
  // it on every dish view it would steal rows from the ingest (and vice-versa),
  // so neither would get a complete picture. The ingest is now the SOLE stream
  // reader; the SPA always reads the durable store.
  // Fail-safe: no stored samples yet (credential not onboarded, or the dish
  // isn't reporting) returns a "pending" shape rather than throwing — telemetry
  // widgets show "not available yet", never hang.
  try {
    const hist = await getTelemetryHistory(deviceId, '24h');
    if (hist?.telemetryAvailable && Array.isArray(hist.points) && hist.points.length > 0) {
      const p = hist.points[hist.points.length - 1];
      return {
        deviceId,
        telemetryAvailable: true,
        popPingLatencyMs: p.latencyMs ?? null,
        downlinkThroughputMbps: p.downMbps ?? null,
        uplinkThroughputMbps: p.upMbps ?? null,
        obstructionFractionPercent: p.obstructionPct ?? null,
        linkQuality: p.linkQuality ?? null,
        pingDropPct: p.pingDropPct ?? null,
        fetchedAt: p.t || new Date().toISOString(),
      };
    }
    return { deviceId, telemetryAvailable: false, pending: true, reason: hist?.reason || null };
  } catch (e) {
    console.error('[starlink-api] getTelemetry failed:', e?.message);
    return { deviceId, telemetryAvailable: false, pending: true, reason: null };
  }
}

/**
 * Telemetry HISTORY across a range — backed by the persistent ingestion
 * store (telemetry_rollup_15m), filled by the telemetry-ingest Lambda.
 * range: '24h' | '7d' | '30d'.
 *
 * Returns `{ telemetryAvailable, reason, points: [{ t, latencyMs,
 * p95LatencyMs, downMbps, upMbps, obstructionPct, pingDropPct,
 * linkQuality }] }` — always an object when the request itself succeeded,
 * even with an empty `points` array, so the caller can see WHY there's no
 * data (`reason: 'no-credential' | 'awaiting-first-report' |
 * 'store-unavailable'`) instead of just a bare "nothing here." Only
 * returns `null` for mock/demo mode (caller synthesizes its own series)
 * or a hard request failure (network/5xx) — genuinely nothing to report.
 */
export async function getTelemetryHistory(deviceId, range = '24h') {
  if (isMockMode()) return null;
  try {
    const data = await liveGet(`/terminals/${encodeURIComponent(deviceId)}/telemetry/history?range=${encodeURIComponent(range)}`);
    if (!data) return null;
    if (!data.telemetryAvailable || !Array.isArray(data.points)) return { telemetryAvailable: false, reason: data.reason || null, points: [] };
    return data;
  } catch {
    // Hard request failure → caller synthesizes.
    return null;
  }
}

/**
 * Get current + historical billing for an account.
 * Maps to /billing-cycles/query and /service-lines/{id}/billing-cycle/all.
 * Pricing is sanitized at the boundary (Guardrail #7).
 */
export async function getBilling(accountNumber) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    // Synthesize billing from this vertical's fleet data
    const fleet = resolveFleet(accountNumber);
    if (fleet) {
      const lines = fleet.terminals.slice(0, 25).map((t) => ({
        serviceLineNumber: t.serviceLineNumber,
        nickname: t.siteName,
        planName: t.planTier,
        planChargeUsd: t.monthlyCostUsd - 40,
        terminalAccessChargeUsd: 40,
        topUpsUsd: t.monthlyUsageGB > t.monthlyAllocationGB ? 25 : 0,
        subtotalUsd: t.monthlyCostUsd + (t.monthlyUsageGB > t.monthlyAllocationGB ? 25 : 0),
      }));
      const total = lines.reduce((s, l) => s + l.subtotalUsd, 0);
      return {
        currentCycle: {
          startedAt: '2026-05-01T00:00:00Z',
          endsAt: '2026-06-01T00:00:00Z',
          estimatedTotalUsd: +total.toFixed(2),
          lines,
        },
        history: [
          { cycleStart: '2026-04-01', totalUsd: +(total * 0.94).toFixed(2), status: 'paid' },
          { cycleStart: '2026-03-01', totalUsd: +(total * 0.89).toFixed(2), status: 'paid' },
          { cycleStart: '2026-02-01', totalUsd: +(total * 0.86).toFixed(2), status: 'paid' },
        ],
      };
    }
    const raw = billingMock[accountNumber];
    if (!raw) return null;
    // Sanitize each line through the pricing guardrail
    return {
      ...raw,
      currentCycle: raw.currentCycle && {
        ...raw.currentCycle,
        lines: raw.currentCycle.lines.map((l) => sanitizePricing({
          ...l,
          // Whitelist the line-display fields we want to keep
          serviceLineNumber: l.serviceLineNumber,
          nickname: l.nickname,
          planName: l.planName,
          planChargeUsd: l.planChargeUsd,
          terminalAccessChargeUsd: l.terminalAccessChargeUsd,
          topUpsUsd: l.topUpsUsd,
          subtotalUsd: l.subtotalUsd,
        })),
      },
    };
  }
  // CORRECTED (2026-05-29 research): billing is a POST, not GET. The
  // Starlink V2 swagger explicitly uses POST + body for the billing-
  // cycles/query endpoint because the date-range parameters and any
  // filters are submitted in the request body, not the URL. Previously
  // we called this as GET which would 404/405 on first live cutover.
  // Path: POST /public/v1/accounts/{accountNumber}/billing-cycles/query
  // Customer-facing billing isn't wired to a live route yet — fail-safe to
  // null so the Billing page renders its "no billing data" empty state
  // instead of throwing a CORS/network error that could blank the page.
  try {
    const data = await livePost('/billing/cycles/query', {});
    return data;
  } catch (e) {
    console.error('[starlink-api] getBilling unavailable:', e?.message);
    return null;
  }
}

// ─── Write operations (limited to what V2 API permission allows) ─

/**
 * Reboot a terminal. § Device Command and Configuration permission.
 *
 * IMPORTANT (2026-05-29 research): the Starlink V2 reboot endpoint is
 * keyed on `routerId`, NOT `deviceId`. They are different IDs on the
 * same physical terminal (deviceId identifies the dish/user-terminal;
 * routerId identifies the WAN router config). Our UI passes the more
 * familiar `deviceId` because that's what shows on every other screen.
 *
 * Resolution path:
 *   1. UI passes deviceId (unchanged).
 *   2. This function looks up the routerId on the terminal record.
 *   3. Lambda calls POST /public/v1/account/{accountNumber}/routers/{routerId}/reboot.
 *
 * If the terminal record doesn't yet carry routerId (older mock data),
 * we fall back to passing deviceId — the Lambda has a server-side lookup
 * to translate. This keeps the demo working while the data model catches up.
 */
// ── Service-line + device WRITES (real backend routes) ──────────
// These hit the live Lambda routes which call the Peplink Partner API
// (sanctioned by our ASP 2.0 distributor). Mock mode short-circuits so the
// demo stays hermetic. Each takes the service-line number / device id as {id}.

/** REAL reboot via the customer's own Starlink V2 Service Account.
 *  target: 'terminal' (the dish, default) | 'router'. */
export async function rebootTerminal(accountNumber, deviceId, routerId) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return { success: true, dispatched: true, message: 'Reboot command queued (mock).' };
  }
  return livePost(`/terminals/${encodeURIComponent(deviceId)}/reboot`,
    routerId ? { target: 'router', routerId } : { target: 'terminal' });
}

/** Set the service-line nickname (immediate Peplink write). */
export async function setServiceLineNickname(serviceLineNumber, nickname) {
  if (isMockMode()) { await wait(MOCK_LATENCY_MS); return { success: true, nickname }; }
  return livePut(`/service-lines/${encodeURIComponent(serviceLineNumber)}/nickname`, { nickname });
}

/** Opt out of auto priority-data top-up (immediate). */
export async function optOutServiceLine(accountNumber, serviceLineNumber) {
  if (isMockMode()) { await wait(MOCK_LATENCY_MS); return { success: true, optedIn: false }; }
  return livePost(`/service-lines/${encodeURIComponent(serviceLineNumber)}/opt-out`, {});
}

/** Opt back in to auto priority-data top-up (immediate). */
export async function optInServiceLine(accountNumber, serviceLineNumber) {
  if (isMockMode()) { await wait(MOCK_LATENCY_MS); return { success: true, optedIn: true }; }
  return livePost(`/service-lines/${encodeURIComponent(serviceLineNumber)}/opt-in`, {});
}

/** Cancel a service line. isCancelNow=false → end of cycle (undo via resume). */
export async function cancelServiceLine(serviceLineNumber, isCancelNow = false) {
  if (isMockMode()) { await wait(MOCK_LATENCY_MS); return { success: true, cancelled: true, isCancelNow }; }
  return livePost(`/service-lines/${encodeURIComponent(serviceLineNumber)}/cancel`, { isCancelNow });
}

/** Undo a pending end-of-cycle cancellation. */
export async function resumeServiceLine(serviceLineNumber) {
  if (isMockMode()) { await wait(MOCK_LATENCY_MS); return { success: true, resumed: true }; }
  return livePost(`/service-lines/${encodeURIComponent(serviceLineNumber)}/resume`, {});
}

/**
 * Update terminal nickname.
 */
export async function updateServiceLineNickname(accountNumber, serviceLineNumber, nickname) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return { success: true, nickname };
  }
  // Real route (Peplink immediate write): PUT /service-lines/{id}/nickname.
  return livePut(`/service-lines/${encodeURIComponent(serviceLineNumber)}/nickname`, { nickname });
}

// ─── Helpers ──────────────────────────────────────────────────

export function isOnline(terminal, telemetry) {
  if (!terminal) return false;
  // Live telemetry is the strongest signal available and is checked FIRST:
  // Starlink only reports telemetry for a dish that is actually powered on
  // and connected right now, so telemetryAvailable:true is direct proof of
  // "online" regardless of what Peplink's account/service-line metadata
  // says. Confirmed real 2026-08-06 — a customer reported two dishes
  // labeled "Offline" that were fully deployed and online; both had live
  // 24h telemetry (latency, throughput, sparkline) while Peplink's `online`
  // flag was simply null for them (that flag reflects service-line
  // assignment, not live connectivity, and can be unset for reasons
  // unrelated to whether the dish is actually up).
  if (telemetry?.telemetryAvailable) return true;
  // The Peplink Partner API gives an explicit in-service flag (`online`,
  // derived from the active/provisioned service line). It is the source of
  // truth for "is this dish in service" BEFORE a per-customer Starlink
  // telemetry credential is onboarded — at which point lastHeartbeatAt fills
  // in and gives true real-time freshness. Honor the explicit flag first so a
  // live, assigned, SYNCED terminal counts as online even with no telemetry yet.
  if (typeof terminal.online === 'boolean') return terminal.online;
  if (terminal.status === 'online' || terminal.status === 'active') return true;
  if (!terminal.lastHeartbeatAt) return false;
  const last = new Date(terminal.lastHeartbeatAt).getTime();
  // Consider online if heartbeat within last 5 minutes
  return Date.now() - last < 5 * 60 * 1000;
}

export function formatBytes(gb) {
  if (gb == null) return '—';
  if (gb < 1) return `${(gb * 1024).toFixed(0)} MB`;
  if (gb < 1024) return `${gb.toFixed(1)} GB`;
  return `${(gb / 1024).toFixed(2)} TB`;
}

export function formatUsd(amount) {
  if (amount == null) return '—';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
